import { AimdocComponentContext } from '@aimdoc/sdk-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Plug, Plus } from 'lucide-react';
import AgentTabHeader from './AgentTabHeader';
import { McpServersTabProps } from './types';
import McpBindingManager from '../../integrations/McpBindingManager';
import { McpEntry } from '../../integrations/mcpServerRegistry';

/**
 * Agent-builder MCP tab: a binding manager over the organization's MCP
 * servers (MCP_SERVERS_PLAN.md). Agent.tools.mcp holds bindings
 * ({mcp_server_id, slug, enabled, enabled_tools}); the server rows —
 * URL, credential, discovered catalog — live at the org level and are
 * managed under Integrations → MCP Servers.
 */
export default function McpServersTab({ actions, setActions, setNeedsUpdate }: McpServersTabProps) {
  const entries: McpEntry[] = Array.isArray(actions.mcp) ? actions.mcp : [];

  const updateEntries = (next: McpEntry[]) => {
    setActions({ ...actions, mcp: next });
    setNeedsUpdate(true);
  };

  // Browser tools (CLIENT_TOOLS_PLAN.md): tools the customer's own app
  // registers in the page through the SDK. Off by default; stored on
  // Agent.tools.client_tools next to the MCP bindings.
  const browserToolsEnabled = actions.client_tools?.enabled === true;
  const setBrowserToolsEnabled = (enabled: boolean) => {
    setActions({ ...actions, client_tools: { ...(actions.client_tools || {}), enabled } });
    setNeedsUpdate(true);
  };

  return (
    <div className="w-full">
      <AimdocComponentContext
        componentDescription="You are in the MCP Servers tab of the agent builder. Here you can give your agent tools from MCP servers connected to your organization, and choose which tools it may use."
        componentName="MCP Servers Tab"
      />

      <div className="mx-auto w-full max-w-4xl py-6">
        <McpBindingManager
          entries={entries}
          onChange={updateEntries}
          consumerLabel="agent"
          renderAddButton={(open) => (
            <AgentTabHeader
              icon={Plug}
              title="MCP Servers"
              description="Give your agent tools from your own product. Servers are connected once for your organization; pick which tools this agent can use."
              actions={(
                <Button type="button" variant="outline" size="sm" onClick={open}>
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  Add MCP Server
                </Button>
              )}
            />
          )}
        />

        <div className="mt-8 rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="space-y-1">
              <Label htmlFor="browser-tools-enabled" className="text-sm font-medium cursor-pointer">
                Browser tools
              </Label>
              <p className="text-xs text-muted-foreground">
                Let this agent call tools your app registers in the page with the Aimdoc SDK
                (<code className="font-mono">aimdoc.registerTool</code>). They run in the user's browser, so
                they suit what only the page can do, such as showing a result in your own UI.
              </p>
            </div>
            <Switch
              id="browser-tools-enabled"
              checked={browserToolsEnabled}
              onCheckedChange={setBrowserToolsEnabled}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
